import React, { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import API from '../lib/api';
import { usePeriod } from '../contexts/PeriodContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { StatusChip } from '../components/app/StatusChip';
import { toast } from 'sonner';
import { Upload, CheckCircle2, AlertTriangle, RefreshCw, File, X, ChevronRight } from 'lucide-react';

const STEPS = ['Select File', 'Enter Password', 'Uploading', 'Validation', 'Review Staging', 'Promote'];

export default function ImportPage() {
  const { periods, activePeriod, fetchPeriods } = usePeriod();
  const [step, setStep]         = useState(0);
  const [file, setFile]         = useState(null);
  const [periodId, setPeriodId] = useState(activePeriod?.id || '');
  const [cardholderId, setCardholderId] = useState('');
  const [cardholders, setCardholders]   = useState([]);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [stagingRows, setStagingRows]   = useState([]);
  const [promoting, setPromoting]       = useState(false);
  const [importHistory, setImportHistory] = useState([]);

  useEffect(() => {
    API.get('/cardholders').then(r => setCardholders(r.data)).catch(() => {});
    API.get('/imports').then(r => setImportHistory(r.data)).catch(() => {});
    if (activePeriod) setPeriodId(activePeriod.id);
  }, [activePeriod?.id]);

  const onDrop = useCallback((accepted) => {
    if (accepted.length === 0) return;
    const f = accepted[0];
    setFile(f);
    if (f.name.toLowerCase().endsWith('.zip')) {
      setNeedsPassword(true);
      setStep(1);
    } else {
      setNeedsPassword(false);
      setStep(1);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'application/zip': ['.zip'] },
    maxFiles: 1,
  });

  const doUpload = async () => {
    if (!file || !periodId) {
      toast.error('Please select a file and a statement period');
      return;
    }
    setUploading(true);
    setStep(2);

    const fd = new FormData();
    fd.append('file', file);
    fd.append('period_id', periodId);
    if (cardholderId) fd.append('cardholder_id', cardholderId);
    if (needsPassword && password) fd.append('zip_password', password);

    try {
      const res = await API.post('/imports/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(res.data);
      setStep(3);

      // Load staging rows
      const staging = await API.get(`/imports/${res.data.import_id}/staging`);
      setStagingRows(staging.data);
      setStep(4);
      toast.success(`Uploaded ${res.data.total_rows} transactions`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
      setStep(0);
    } finally {
      setUploading(false);
    }
  };

  const doPromote = async () => {
    if (!uploadResult?.import_id) return;
    setPromoting(true);
    try {
      const res = await API.post(`/imports/${uploadResult.import_id}/promote`);
      toast.success(`Promoted ${res.data.promoted} transactions to workbench!`);
      setStep(5);
      fetchPeriods();
      API.get('/imports').then(r => setImportHistory(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Promote failed');
    } finally {
      setPromoting(false);
    }
  };

  const reset = () => {
    setStep(0); setFile(null); setUploadResult(null); setStagingRows([]);
    setPassword(''); setNeedsPassword(false);
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-xl font-bold">Import Statement</h1>

      {/* Stepper */}
      <div className="flex items-center gap-1" data-testid="import-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium
              ${step > i ? 'bg-emerald-100 text-emerald-700' : step === i ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
              {step > i ? <CheckCircle2 size={12} /> : <span>{i + 1}</span>}
              <span className="hidden sm:block">{s}</span>
            </div>
            {i < STEPS.length - 1 && <ChevronRight size={12} className="text-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload form */}
        <div className="lg:col-span-2 space-y-4">
          {step < 4 && (
            <div className="bg-white rounded-lg border shadow-sm p-5 space-y-4">
              {/* Period & Cardholder selects */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Statement Period *</Label>
                  <Select value={periodId} onValueChange={setPeriodId}>
                    <SelectTrigger className="mt-1 h-8 text-sm">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {periods.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Cardholder (optional)</Label>
                  <Select value={cardholderId} onValueChange={setCardholderId}>
                    <SelectTrigger className="mt-1 h-8 text-sm">
                      <SelectValue placeholder="All / Not linked" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="">Not linked</SelectItem>
                      {cardholders.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dropzone */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                  ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}
                data-testid="import-dropzone"
              >
                <input {...getInputProps()} />
                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <File size={32} className="text-emerald-600" />
                    <div className="text-sm font-medium">{file.name}</div>
                    <div className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)} KB</div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload size={32} className="text-slate-300" />
                    <div className="text-sm text-slate-600">Drag & drop CSV or ZIP file here</div>
                    <div className="text-xs text-slate-400">or click to browse &middot; .csv or .zip accepted</div>
                  </div>
                )}
              </div>

              {/* Password field for ZIP */}
              {needsPassword && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <Label className="text-xs font-semibold text-amber-800">ZIP Password Required</Label>
                  <p className="text-xs text-amber-700 mt-0.5 mb-2">This file appears to be a password-protected ZIP. Enter the password (usually the full card number).</p>
                  <Input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter ZIP password (e.g. card number)"
                    className="h-8 text-sm"
                    data-testid="import-zip-password-input"
                  />
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2">
                {file && (
                  <Button variant="outline" size="sm" onClick={reset}>
                    <X size={13} className="mr-1" /> Clear
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={doUpload}
                  disabled={!file || !periodId || uploading}
                  className="ml-auto"
                  data-testid="import-upload-submit-button"
                >
                  {uploading ? <><RefreshCw size={13} className="animate-spin mr-1" /> Uploading…</> : <><Upload size={13} className="mr-1" /> Upload & Validate</>}
                </Button>
              </div>
            </div>
          )}

          {/* Upload progress */}
          {step === 2 && (
            <div className="bg-white rounded-lg border shadow-sm p-5">
              <div className="flex items-center gap-2 mb-3">
                <RefreshCw size={14} className="animate-spin text-emerald-600" />
                <span className="text-sm font-medium">Processing…</span>
              </div>
              <Progress value={50} className="h-1.5" />
            </div>
          )}

          {/* Validation report */}
          {uploadResult && step >= 3 && (
            <div className="bg-white rounded-lg border shadow-sm p-5" data-testid="import-validation-summary">
              <h3 className="text-sm font-semibold mb-3">Validation Report</h3>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-emerald-50 border border-emerald-100 rounded p-3 text-center">
                  <div className="text-xl font-bold text-emerald-600">{uploadResult.valid_rows}</div>
                  <div className="text-xs text-emerald-700">Valid Rows</div>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded p-3 text-center">
                  <div className="text-xl font-bold text-amber-600">{uploadResult.skipped_rows}</div>
                  <div className="text-xs text-amber-700">Skipped</div>
                </div>
                <div className="bg-slate-50 border border-slate-100 rounded p-3 text-center">
                  <div className="text-xl font-bold text-slate-600">{uploadResult.total_rows}</div>
                  <div className="text-xs text-slate-700">Total</div>
                </div>
              </div>
              {/* Statement metadata */}
              <div className="bg-slate-50 rounded p-3 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Statement Period</span><span className="font-medium">{uploadResult.metadata?.statement_period || '—'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Cardholder</span><span className="font-medium">{uploadResult.metadata?.cardholder_name || '—'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Masked Card</span><span className="font-mono">{uploadResult.metadata?.masked_card || '—'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Closing Balance</span><span className="font-bold">{uploadResult.metadata?.closing_balance?.toLocaleString() || '—'}</span></div>
              </div>
              {/* Skipped rows */}
              {(uploadResult.skipped_row_details || []).length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs text-amber-700 cursor-pointer">{uploadResult.skipped_rows} skipped row(s) - click to expand</summary>
                  <div className="mt-2 space-y-1">
                    {uploadResult.skipped_row_details.map((r, i) => (
                      <div key={i} className="text-xs bg-amber-50 p-1.5 rounded flex gap-2">
                        <AlertTriangle size={10} className="text-amber-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{r.reason}: {r.line?.slice(0, 60)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* Staging table */}
          {step >= 4 && stagingRows.length > 0 && (
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-sm font-semibold">Staged Transactions ({stagingRows.length})</h3>
                {step === 4 && (
                  <Button size="sm" onClick={doPromote} disabled={promoting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="import-promote-button">
                    {promoting ? <><RefreshCw size={13} className="animate-spin mr-1" /> Promoting…</> : <><CheckCircle2 size={13} className="mr-1" /> Promote to Transactions</>}
                  </Button>
                )}
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="data-table">
                  <thead><tr>
                    <th>Date</th><th>Description</th><th className="text-right">Amount</th><th>Type</th>
                  </tr></thead>
                  <tbody>
                    {stagingRows.slice(0, 100).map((r, i) => (
                      <tr key={i} className={r.is_credit ? 'text-emerald-700' : ''}>
                        <td className="whitespace-nowrap">{r.date}</td>
                        <td className="max-w-xs truncate" title={r.description_raw}>{r.description_raw}</td>
                        <td className={`text-right tabular-nums font-medium ${r.amount < 0 ? 'text-emerald-600' : ''}`}>{r.amount?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                        <td>{r.is_fee ? <span className="chip-pending text-xs px-1.5 py-0.5 rounded">Fee</span> : r.is_credit ? <span className="chip-approved text-xs px-1.5 py-0.5 rounded">Credit</span> : <span className="chip-coded text-xs px-1.5 py-0.5 rounded">Debit</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Success */}
          {step === 5 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
              <CheckCircle2 size={32} className="text-emerald-500 mx-auto mb-2" />
              <h3 className="text-sm font-semibold text-emerald-800">Import Complete!</h3>
              <p className="text-xs text-emerald-700 mt-1">Transactions have been promoted and suggestions applied. Head to the Workbench to review.</p>
              {uploadResult?.skipped_linked > 0 && (
                <p className="text-xs text-violet-700 mt-1 font-medium">
                  {uploadResult.skipped_linked} international transaction fee rows were merged into their parent transactions.
                </p>
              )}
              {uploadResult?.skipped_excluded > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  {uploadResult.skipped_excluded} payment/balance row(s) excluded from allocation.
                </p>
              )}
              <div className="flex gap-2 justify-center mt-4">
                <Button variant="outline" size="sm" onClick={reset}>Import Another</Button>
                <Button size="sm" onClick={() => window.location.href = '/workbench'}>Go to Workbench</Button>
              </div>
            </div>
          )}
        </div>

        {/* Import History */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg border shadow-sm p-4">
            <h3 className="text-sm font-semibold mb-3">Import History</h3>
            {importHistory.length === 0 ? (
              <p className="text-xs text-slate-400">No imports yet</p>
            ) : (
              <div className="space-y-2">
                {importHistory.slice(0, 10).map(imp => (
                  <div key={imp.id} className="border rounded p-2.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate max-w-32">{imp.filename}</span>
                      <StatusChip status={imp.status} size="xs" />
                    </div>
                    <div className="text-slate-500 mt-0.5">{imp.total_rows} rows • {imp.uploaded_by}</div>
                    <div className="text-slate-400">{imp.uploaded_at ? new Date(imp.uploaded_at).toLocaleDateString() : ''}</div>
                    {imp.status === 'PROMOTED' && (
                      <button
                        onClick={async () => {
                          try {
                            const API2 = (await import('../lib/api')).default;
                            const res = await API2.post(`/imports/${imp.id}/reprocess-fees`);
                            alert(res.data.message);
                            API2.get('/imports').then(r => setImportHistory(r.data)).catch(() => {});
                          } catch(e) { alert('Reprocess failed: ' + (e.response?.data?.detail || e.message)); }
                        }}
                        className="mt-1.5 text-[10px] text-violet-600 hover:text-violet-800 font-semibold flex items-center gap-1"
                        title="Re-link international transaction fees to parent transactions"
                      >
                        <RefreshCw size={9} /> Reprocess Fee Linking
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
